<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { useEndpoint, useToastNotifications } from '@apparatix/composables';
import { Stack, Typography } from '@apparatix/library/core';
import FileUploader from '@apparatix/library/FileUploader.vue';
import { button as greenButton } from '@apparatix/theme/Green';
import type { OfferSubmissionMode } from '@apparatix/types/Documents';
import { formCopy } from './offerSubmissionCopy';

/**
 * Shared body of the Submit Offer / Submit Contract flow: one signed PDF in,
 * one POST to the matching offer-execution stage. Hosted by SubmitOfferModal
 * (Sales Documents flyout) and ProposalSubmitOffer (Solution Center slide-in).
 */
type Props = {
  proposalId: number;
  mode: OfferSubmissionMode;
  isEssentials?: boolean;
};

type Emits = {
  (e: 'submitted'): void;
  (e: 'cancel'): void;
};

interface OfferExecutionResponse {
  success: boolean;
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isEssentials: false,
});
const emit = defineEmits<Emits>();

const MAX_FILE_SIZE_MB = 50;

const files = ref<File[]>([]);
const selectedFile = computed(() => files.value[0] ?? null);
const blockedError = ref<string | null>(null);
// Once a submission has succeeded the form is done: the host closes it, but
// the button must not accept further clicks while that happens.
const hasSubmitted = ref(false);

const copy = computed(() => formCopy(props.mode, props.isEssentials));

const { showSuccess, showError } = useToastNotifications();

const { isLoading, makeRequest, setEndpointUrl } = useEndpoint<OfferExecutionResponse, OfferExecutionResponse>({
  type: 'POST',
  successField: 'success',
  toast: { showOnSuccess: false, showOnError: false },
  onSuccess: response => {
    hasSubmitted.value = true;
    showSuccess(response?.message ?? copy.value.successMessage, copy.value.successTitle);
    emit('submitted');
  },
  onError: errorData => {
    // Drop the rejected attachment so the button cannot re-send the same
    // file; the reason stays visible under the uploader until a new pick.
    const message = errorData?.message ?? copy.value.errorMessage;
    files.value = [];
    blockedError.value = message;
    showError(message, copy.value.errorTitle);
  },
});

const onFilesChanged = () => blockedError.value = null;

const canSubmit = computed(() => !!selectedFile.value && !isLoading.value && !hasSubmitted.value);

const submit = () => {
  if (isLoading.value || hasSubmitted.value) {
    return;
  }

  if (!selectedFile.value) {
    blockedError.value = 'Attach the signed PDF to continue.';
    return;
  }

  blockedError.value = null;

  const payload = new FormData();
  payload.append('pdf', selectedFile.value);

  setEndpointUrl(`/offer-execution/${props.proposalId}/${props.mode === 'contract' ? 'stage2' : 'stage1'}`);
  makeRequest(payload);
};
</script>

<template>
  <div class="flex flex-col">
    <Stack :gap="4" class="p-4">
      <Stack :gap="1">
        <Typography variant="form-heading">
          {{ copy.heading }}
        </Typography>
        <Typography variant="body" muted>
          {{ copy.body }}
        </Typography>
      </Stack>

      <Message v-if="copy.warning" severity="warn">
        {{ copy.warning }}
      </Message>

      <Stack :gap="2">
        <Typography variant="form-label">
          Signed PDF (required)
        </Typography>
        <FileUploader
          v-model="files"
          :max-files="1"
          :max-individual-size-mb="MAX_FILE_SIZE_MB"
          accept="application/pdf"
          choose-label="Choose PDF"
          :disabled="isLoading || hasSubmitted"
          @file-added="onFilesChanged"
          @file-removed="onFilesChanged"
        />
        <Typography variant="small-body" muted>
          PDF only · up to 50 MB · one file
        </Typography>
        <Typography v-if="blockedError" variant="form-error">
          {{ blockedError }}
        </Typography>
      </Stack>
    </Stack>

    <div class="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-[var(--p-content-border-color)]">
      <div class="flex gap-2">
        <Button
          severity="secondary"
          :disabled="isLoading"
          @click="emit('cancel')"
        >
          Cancel
        </Button>
        <Button
          :dt="greenButton"
          :disabled="!canSubmit"
          :loading="isLoading"
          @click="submit"
        >
          {{ copy.confirmLabel }}
        </Button>
      </div>
    </div>
  </div>
</template>
